import { ActivatedRoute } from '@angular/router';
import { Type, Injector } from '@angular/core';
import { DialogService } from 'primeng/dynamicdialog';
import { INgxGridColumn, INgxGridResult, INgxGridFilter, INgxGridInstance } from 'ngx-primeng-grid';

import { FormBaseComponent } from '../../core/_base/form.base';
import { INgxManageLayout, INgxManageLayoutAction, INgxManageLayoutDialogData, INgxManageLayoutSetting, INgxManageLayoutSettingAddOption } from '../models/ngx-manage-layout.model';
import { NgxUtility } from '../../core/_utility/utility';
import { NgxManageLayoutService } from '../services/ngx-manage-layout.service';
import { Observable } from 'rxjs';
import { INgxResult, INgxFilter } from '../../core/public-api';
import { IBreadcrumb } from '../../ngx-wrapper-shared/models/breadcrumb';
import * as _ from 'lodash';

export abstract class NgxManageLayoutBaseComponent<TModel, TFilterModel> extends FormBaseComponent<TModel>{

    loading: boolean = false;
    // layout
    layout: INgxManageLayout;
    // columns
    columns: INgxGridColumn[] = [];
    // result
    result: INgxGridResult<TModel> = { results: [], total: 0 };
    // filter
    filter: INgxFilter<TFilterModel>;
    // breadcrumbs
    breadcrumbs: IBreadcrumb[] = [];
    // selected rows
    selectedRows: any[] = [];

    // servie declaration
    private manageDialog: DialogService;
    //
    private ngxLayoutBaseService: NgxManageLayoutService;
    //
    private activatedBaseRoute: ActivatedRoute;

    // abstract metods
    protected abstract getSearchService(filter: INgxFilter<TFilterModel>): Observable<INgxGridResult<TModel>>;
    protected abstract getDeleteService(model: TModel): Observable<INgxResult>;
    protected abstract getChangeStatusService(model: TModel): Observable<INgxResult>;
    protected abstract getManageDialogComponent(): Type<any>;
    protected abstract handleRowAction(instance: INgxGridInstance<TModel>): void;
    protected abstract getExtraDialogData(): any;

    // get manage component

    constructor(manageInjector: Injector) {
        super(manageInjector);
        // init instance
        this.manageDialog = manageInjector.get(DialogService);
        this.ngxLayoutBaseService = manageInjector.get(NgxManageLayoutService);
        this.activatedBaseRoute = manageInjector.get(ActivatedRoute);
    }

    // prepare layout from router
    protected prepareLayoutFromRouter(invokeSearch: boolean = true, dataKey: string = 'layout') {
        const data = this.activatedBaseRoute.snapshot.data;
        if (NgxUtility.hasPropertyValue(data, dataKey)) {
            const manageJson = data[dataKey];
            // prepare layout
            this.prepareLayout(manageJson, invokeSearch);
        }
    }

    // prepare layout from service
    protected prepareLayoutFromService(jsonObjectService: Observable<any>, successCallback: (jsonObject: any) => void, invokeSearch: boolean = true) {
        const ref = jsonObjectService.subscribe(jsonObject => {
            // on success result
            successCallback(jsonObject);
            // prepare layout
            this.prepareLayout(jsonObject, invokeSearch);
        });
        this.subscription.add(ref);
    }

    // prepare layout from json
    protected prepareLayout(jsonObject: any, invokeSearch: boolean) {
        if (!jsonObject) { return; }
        // get the title
        this.layout = this.ngxLayoutBaseService.process(jsonObject);
        // prepare columns
        this.columns = this.layout.columns;
        //override layout
        this.overrideLayout(this.layout);
        // init filter
        this.initFilter(this.layout.setting);
        // override Filter
        this.overrideFilter(this.filter);
        // do search
        if (invokeSearch) {
            this.doSearch();
        }
    }

    // handle action
    handleOnAction($action: INgxManageLayoutAction<TModel>) {
        if ($action.source === 'create') {
            this.openManageDialog(null);
        } else if ($action.source === 'table') {
            this.handleTableAction($action.instance);
        } else if ($action.source === 'custom') {
            this.overrideAction(this.layout.setting.addOption);
        }
    }

    // handle when filter changed
    handleOnFilter($filter: INgxGridFilter) {
        const reqFilter = _.cloneDeep($filter);
        // only set basic filter from grid
        this.filter.pageNo = reqFilter.pageNo;
        this.filter.perPage = reqFilter.perPage;
        this.filter.sortField = reqFilter.sortField;
        this.filter.sortOrder = reqFilter.sortOrder;
        this.doSearch();
    }

    // init filter
    private initFilter(setting: INgxManageLayoutSetting) {
        this.filter = {
            pageNo: 0,
            perPage: setting.perPage,
            sortField: setting.defaultSortColumn,
            sortOrder: setting.defaultSortOrder,
            formFilter: null,
            searchText: null
        }
    }

    // handle when search changed
    handleOnSearch(text: string) {
        // on search reset the page no
        this.filter.pageNo = 0;
        this.filter.searchText = text;
        this.doSearch();
    }

    // handle row selected
    handleOnRowSelected($event: any) {
        this.selectedRows = $event;
    }

    private handleTableAction(instance: INgxGridInstance<TModel>) {
        switch (instance.column.key) {
            case 'edit': {
                this.openManageDialog(instance.row);
            } break;
            case 'delete': {
                this.handleDelete(instance.row);
            } break;
            case 'status': {
                this.handleStatusChange(instance.row);
            } break;
            default: {
                this.handleRowAction(instance);
            } break;
        }
    }

    private openManageDialog(model: TModel) {
        // data model
        const dataModel: INgxManageLayoutDialogData<TModel> = {
            model,
            layout: this.layout,
            data: this.getExtraDialogData()
        };
        // dialog
        const dialogRef = this.manageDialog.open(this.getManageDialogComponent(), {
            header: this.layout.setting.addOption.modalTitle,
            width: this.layout.setting.addOption.modalWidth,
            contentStyle: NgxUtility.dialogContentStyle(),
            data: dataModel
        });
        // handle close event
        const ref = dialogRef.onClose.subscribe(result => {
            // unsubscribe
            ref.unsubscribe();
            // do work
            if (result) {
                this.doSearch();
            }
        });
    }

    protected handleDelete(model: TModel) {
        // pre action
        this.overrideHttpCallPreAction(model);
        const ref = this.getDeleteService(model).subscribe(res => {
            // post action
            this.overrideHttpCallPostAction(res);
            // search
            this.doSearch();
        });
        this.subscription.add(ref);
    }

    protected handleStatusChange(model: TModel) {
        // pre action
        this.overrideHttpCallPreAction(model);
        const ref = this.getChangeStatusService(model).subscribe(res => {
            // post action
            this.overrideHttpCallPostAction(res);
        });
        this.subscription.add(ref);
    }

    // do search
    protected doSearch() {
        this.loading = true;
        const ref = this.getSearchService(this.filter).subscribe(res => {
            this.result = res;
            // reset the selected rows
            this.selectedRows = [];
            // set loading false
            this.loading = false;
        });
        this.subscription.add(ref);
    }

    //  override Layout
    protected overrideLayout($layout: INgxManageLayout) { }

    // override Filter
    protected overrideFilter($filter: INgxFilter<TFilterModel>) { }

    // override Action for custom
    protected overrideAction($option: INgxManageLayoutSettingAddOption) { }

    // override http call pre action
    protected overrideHttpCallPreAction(model: TModel) { }
    // override http call pre action
    protected overrideHttpCallPostAction(result: INgxResult) { }

}